import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { C, cs } from '../styles/common';
import api from '../api';

const TYPE_LABELS = {
  cycle_drift:        'Cycle time vs plan',
  scrap_spike:        'Scrap spike',
  machine_idle:       'Idle capacity',
  tooling_roi:        'Tooling ROI',
  material_low_stock: 'Low stock',
  // From the measuring tool (7 Oct 2026): each card opens its thing on Measured jobs.
  measured_ready:       'Measured: ready to decide',
  measured_moved:       'Measured: look again',
  measured_shaken:      'Measured: check what it rests on',
  measured_display_gap: 'Machine display vs counted output',
  measured_try_steps:   'Measured: worth timing step by step',
  measured_person_gap:  'Measured: one person far from the others',
};

const TYPE_ICONS = {
  cycle_drift:        'fa-solid fa-gauge-high',
  scrap_spike:        'fa-solid fa-triangle-exclamation',
  machine_idle:       'fa-solid fa-circle-pause',
  tooling_roi:        'fa-solid fa-screwdriver-wrench',
  material_low_stock: 'fa-solid fa-box-open',
  measured_ready:       'fa-solid fa-stopwatch',
  measured_moved:       'fa-solid fa-arrows-left-right',
  measured_shaken:      'fa-solid fa-scale-unbalanced',
  measured_display_gap: 'fa-solid fa-gauge-simple-high',
  measured_try_steps:   'fa-solid fa-list-ol',
  measured_person_gap:  'fa-solid fa-user-clock',
};

const SEVERITY_COLOR = {
  critical: { bg: '#7f1d1d', text: '#fca5a5', dot: '#ef4444' },
  warning:  { bg: '#78350f', text: '#fbbf24', dot: '#f59e0b' },
  info:     { bg: '#1e3a5f', text: '#93c5fd', dot: '#3b82f6' },
};

const ACTION_LABELS = {
  view_params:    { label: 'View parameters', path: '/process' },
  view_jobs:      { label: 'View jobs',       path: '/today' },
  view_schedule:  { label: 'View schedule',   path: '/admin/schedule' },
  view_moulds:    { label: 'View moulds',     path: '/admin/moulds' },
  view_materials: { label: 'View materials',  path: '/admin/materials' },
};

const SNOOZE_OPTIONS = [
  { label: '1 week',  days: 7 },
  { label: '2 weeks', days: 14 },
  { label: '1 month', days: 30 },
];

// Insights stored before 6 Oct 2026 carry a model's "# Capacity Insight" title
// and **bold** marks; shown as plain words.
function plainText(text) {
  let t = String(text || '').replace(/\r\n/g, '\n').trim();
  const lines = t.split('\n');
  if (lines.length > 1 && /^\s*(#{1,6}\s|\*\*[^*]+\*\*\s*$)/.test(lines[0])) t = lines.slice(1).join('\n').trim();
  return t
    .replace(/^\s*#{1,6}\s*/gm, '')
    .replace(/\*\*|__/g, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function snoozeDate(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function Insights() {
  const navigate          = useNavigate();
  const { isDirector }    = useAuth();
  const { toast }         = useToast();
  const [insights, setInsights] = useState([]);
  const [tab, setTab]     = useState('active');   // active | snoozed | history
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(null); // insight id with snooze menu open

  const STATUS_MAP = {
    active:  'active',
    snoozed: 'snoozed',
    history: 'dismissed,actioned,expired',
  };

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/v1/insights?status=${STATUS_MAP[tab]}`)
      .then(r => setInsights(r.data))
      .catch(() => setInsights([]))
      .finally(() => setLoading(false));
  }, [tab]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  async function runAnalysis() {
    setRunning(true);
    try {
      const r = await api.post('/api/v1/insights/run');
      toast.success(`Analysis complete — ${r.data.created} new insight(s) found`);
      if (tab === 'active') load();
    } catch {
      toast.error('Analysis failed');
    } finally {
      setRunning(false);
    }
  }

  async function patch(id, body) {
    try {
      await api.patch(`/api/v1/insights/${id}`, body);
      setInsights(prev => prev.filter(i => i.id !== id));
    } catch {
      toast.error('Action failed');
    }
  }

  // "Plan with X s" on a Cycle time vs plan card (POST /insights/:id/apply-cycle).
  async function applyCycle(insight) {
    try {
      const { data } = await api.post(`/api/v1/insights/${insight.id}/apply-cycle`);
      toast.success(data.says);
    } catch (err) {
      toast.error(err.response?.data?.error || 'That did not work. Try again.');
    }
    load();
  }
  function dismiss(id)       { patch(id, { status: 'dismissed' }); }
  function markActioned(id)  { patch(id, { status: 'actioned' });  }
  function snooze(id, days)  {
    patch(id, { status: 'snoozed', snoozed_until: snoozeDate(days) });
    setSnoozeOpen(null);
  }

  const activeSavings = insights
    .filter(i => i.status === 'active' && i.savings_eur_month > 0)
    .reduce((s, i) => s + (i.savings_eur_month || 0), 0);

  if (!isDirector) {
    return (
      <div style={{ ...cs.page, ...cs.center }}>
        <p style={{ color: C.muted }}>Director access required.</p>
      </div>
    );
  }

  return (
    <div style={cs.page}>
      {/* Header */}
      <div style={{ ...cs.pageHeader, marginBottom: '20px' }}>
        <div>
          <h1 style={cs.pageTitle}>Financial Insights</h1>
          <p style={cs.pageSub}>AI-powered recommendations — updated nightly, ranked by estimated monthly impact</p>
        </div>
        <button
          style={{ ...cs.btnPrimary, opacity: running ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
          onClick={runAnalysis}
          disabled={running}
        >
          <i className={running ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-play'} />
          {running ? 'Analysing…' : 'Run analysis now'}
        </button>
      </div>

      {/* Stats bar */}
      {tab === 'active' && !loading && insights.length > 0 && (
        <div style={s.statsBar}>
          <Stat label="Active insights" value={insights.length} />
          <Stat label="Est. monthly savings" value={activeSavings > 0 ? `€${activeSavings.toLocaleString()}` : '—'} highlight />
          <Stat label="Critical" value={insights.filter(i => i.severity === 'critical').length} />
          <Stat label="Warnings" value={insights.filter(i => i.severity === 'warning').length} />
        </div>
      )}

      {/* Tabs */}
      <div style={s.tabs}>
        {[
          { key: 'active',  label: 'Active' },
          { key: 'snoozed', label: 'Snoozed' },
          { key: 'history', label: 'History' },
        ].map(t => (
          <button
            key={t.key}
            style={{ ...s.tab, ...(tab === t.key ? s.tabActive : {}) }}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={cs.center}><p style={{ color: C.muted }}>Loading…</p></div>
      ) : insights.length === 0 ? (
        <Empty tab={tab} onRun={runAnalysis} running={running} />
      ) : (
        <div style={s.cards}>
          {insights.map(insight => (
            <InsightCard
              onApplyCycle={applyCycle}
              key={insight.id}
              insight={insight}
              tab={tab}
              snoozeOpen={snoozeOpen === insight.id}
              onSnoozeToggle={() => setSnoozeOpen(snoozeOpen === insight.id ? null : insight.id)}
              onSnooze={(days) => snooze(insight.id, days)}
              onDismiss={() => dismiss(insight.id)}
              onAction={() => markActioned(insight.id)}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      {tab === 'active' && !loading && !insights.some(i => i.type === 'cycle_drift') && <CycleTimeStatus />}
    </div>
  );
}

// "Cycle time vs plan", when it has nothing to say: why, and what would let
// it (the director, 6 Oct 2026 -- an empty list must not read as "all fine").
function CycleTimeStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get('/api/v1/insights/cycle-time').then(r => setSt(r.data)).catch(() => setSt(null));
  }, []);
  if (!st) return null;
  const onPlan = st.judged.filter(j => j.verdict === 'on plan');
  let says;
  if (st.measured_runs === 0) {
    says = `No part can be judged yet. A part is judged from runs started and ended on the Today page — at least ${st.min_runs} of them — because those record how long the run really took. `
      + (st.finished_runs
        ? `The ${st.finished_runs} run${st.finished_runs === 1 ? '' : 's'} finished in the last 90 days ${st.finished_runs === 1 ? 'was' : 'were'} marked complete from the board, which records the parts made but not the run time.`
        : 'No run has finished in the last 90 days.');
  } else if (!st.judged.length) {
    says = `Not enough measured runs yet: a part needs ${st.min_runs}. `
      + st.waiting.slice(0, 4).map(w => `${w.part || 'A part'}: ${w.runs} of ${st.min_runs}`).join(' · ') + '.';
  } else {
    says = `${onPlan.length} part${onPlan.length === 1 ? ' runs' : 's run'} within ${st.threshold_pct}% of ${onPlan.length === 1 ? 'its' : 'their'} planned cycle time.`
      + (st.waiting.length ? ` ${st.waiting.length} more ${st.waiting.length === 1 ? 'part needs' : 'parts need'} more measured runs.` : '');
  }
  return (
    <div style={{ ...s.card, borderLeft: `3px solid ${C.border}`, marginTop: '16px' }}>
      <div style={s.cardMeta}>
        <span style={{ ...s.typeBadge, background: C.surfaceAlt, color: C.muted }}>
          <i className={TYPE_ICONS.cycle_drift} style={{ marginRight: '5px' }} />
          {TYPE_LABELS.cycle_drift}
        </span>
      </div>
      <p style={{ ...s.cardBody, marginTop: '10px', marginBottom: 0 }}>{says}</p>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Stat({ label, value, highlight }) {
  return (
    <div style={s.stat}>
      <span style={{ ...s.statValue, ...(highlight && value !== '—' ? { color: C.teal } : {}) }}>
        {value}
      </span>
      <span style={s.statLabel}>{label}</span>
    </div>
  );
}

function Empty({ tab, onRun, running }) {
  return (
    <div style={{ ...cs.center, flexDirection: 'column', gap: '12px', paddingTop: '60px' }}>
      <i className="fa-solid fa-lightbulb" style={{ fontSize: '32px', color: C.mutedDark }} />
      <p style={{ color: C.muted, margin: 0 }}>
        {tab === 'active'
          ? 'No active insights — run the analysis to check for opportunities.'
          : tab === 'snoozed'
          ? 'No snoozed insights.'
          : 'No dismissed or actioned insights yet.'}
      </p>
      {tab === 'active' && (
        <button
          style={{ ...cs.btnSecondary, display: 'flex', alignItems: 'center', gap: '6px' }}
          onClick={onRun}
          disabled={running}
        >
          <i className={running ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-play'} />
          {running ? 'Analysing…' : 'Run analysis now'}
        </button>
      )}
    </div>
  );
}

function InsightCard({ insight, tab, snoozeOpen, onSnoozeToggle, onSnooze, onDismiss, onAction, onApplyCycle, navigate }) {
  const sev     = SEVERITY_COLOR[insight.severity] || SEVERITY_COLOR.info;
  const measured = String(insight.type || '').startsWith('measured_');
  const action  = insight.type === 'cycle_drift' && insight.metadata?.part_id
    ? { label: 'Open the part', path: `/admin/parts/${insight.metadata.part_id}` }
    : measured && insight.action_payload?.key
      ? { label: 'Open in Measured jobs', path: `/admin/measuring?tab=measured&key=${encodeURIComponent(insight.action_payload.key)}` }
      : ACTION_LABELS[insight.action_type];
  const boardMin = measured ? insight.metadata?.board_minutes : null;
  const isHistory = tab === 'history';

  return (
    <div style={{ ...s.card, borderLeft: `3px solid ${sev.dot}` }}>
      {/* Card header */}
      <div style={s.cardTop}>
        <div style={s.cardMeta}>
          <span style={{ ...s.typeBadge, background: sev.bg, color: sev.text }}>
            <i className={TYPE_ICONS[insight.type] || 'fa-solid fa-circle-info'} style={{ marginRight: '5px' }} />
            {TYPE_LABELS[insight.type] || insight.type}
          </span>
          {insight.confidence && (
            <span style={s.confidence}>{insight.confidence} confidence</span>
          )}
          {tab === 'snoozed' && insight.snoozed_until && (
            <span style={s.snoozeBadge}>
              Snoozed until {new Date(insight.snoozed_until).toLocaleDateString()}
            </span>
          )}
          {tab === 'history' && (
            <span style={{ ...s.confidence, color: C.muted }}>
              {insight.status === 'actioned' ? 'Actioned' : insight.status === 'dismissed' ? 'Dismissed' : 'Expired'}
            </span>
          )}
        </div>
        {insight.savings_eur_month > 0 && (
          <div style={s.savings}>
            <span style={s.savingsValue}>€{insight.savings_eur_month.toLocaleString()}</span>
            <span style={s.savingsLabel}>/mo</span>
          </div>
        )}
        {boardMin != null && Math.abs(boardMin) >= 1 && (
          <div style={s.savings} title={`Machine time on the board over the next ${insight.metadata.board_weeks || 4} weeks if it is applied`}>
            <span style={{ ...s.savingsValue, color: boardMin < 0 ? C.teal : '#f59e0b' }}>{boardMin < 0 ? '−' : '+'}{Math.abs(boardMin) >= 60 ? `${Math.round(Math.abs(boardMin) / 6) / 10} h` : `${Math.abs(boardMin)} min`}</span>
            <span style={s.savingsLabel}>/{insight.metadata.board_weeks || 4} wk</span>
          </div>
        )}
        {insight.type === 'cycle_drift' && insight.metadata?.direction === 'faster' && insight.metadata?.hours_month > 0 && (
          <div style={s.savings} title="Machine time the board books for this part but does not need">
            <span style={{ ...s.savingsValue, color: C.teal }}>≈ {insight.metadata.hours_month} h</span>
            <span style={s.savingsLabel}>/mo freed</span>
          </div>
        )}
      </div>

      {/* Title */}
      <h3 style={s.cardTitle}>{insight.title}</h3>

      {/* Body */}
      <p style={s.cardBody}>{plainText(insight.body)}</p>

      {/* Actions */}
      {!isHistory && (
        <div style={s.cardActions}>
          {action && (
            <button
              style={{ ...cs.btnSecondary, fontSize: '12px', padding: '6px 14px' }}
              // A measuring card closes itself once it is decided on Measured jobs.
              onClick={() => { if (!measured) onAction(); navigate(action.path); }}
            >
              <i className="fa-solid fa-arrow-up-right-from-square" style={{ marginRight: '5px' }} />
              {action.label}
            </button>
          )}
          {/* Close the loop: the floor's cycle becomes the part's, for good. */}
          {insight.type === 'cycle_drift' && insight.metadata?.measured_cycle_s && onApplyCycle && (
            <button
              style={{ ...cs.btnSecondary, fontSize: '12px', padding: '6px 14px', color: C.teal, borderColor: C.teal }}
              title={`Set ${insight.metadata.part_name}'s cycle time to ${insight.metadata.measured_cycle_s} s, the middle of ${insight.metadata.runs} runs on the floor. Recorded in the part's history.`}
              onClick={() => onApplyCycle(insight)}
            >
              <i className="fa-solid fa-check" style={{ marginRight: '5px' }} />
              Plan with {insight.metadata.measured_cycle_s} s
            </button>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', position: 'relative' }}>
            {/* Snooze with dropdown */}
            {tab === 'active' && (
              <div style={{ position: 'relative' }}>
                <button style={s.ghostBtn} onClick={onSnoozeToggle}>
                  <i className="fa-solid fa-clock" style={{ marginRight: '4px' }} />
                  Snooze
                  <i className="fa-solid fa-chevron-down" style={{ marginLeft: '4px', fontSize: '10px' }} />
                </button>
                {snoozeOpen && (
                  <div style={s.snoozeMenu}>
                    {SNOOZE_OPTIONS.map(opt => (
                      <button key={opt.days} style={s.snoozeItem} onClick={() => onSnooze(opt.days)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button style={{ ...s.ghostBtn, color: C.muted }} onClick={onDismiss}>
              <i className="fa-solid fa-xmark" style={{ marginRight: '4px' }} />
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  statsBar: {
    display: 'flex', gap: '1px', background: C.border,
    borderRadius: '8px', overflow: 'hidden', marginBottom: '20px',
  },
  stat: {
    flex: 1, background: C.surface, padding: '12px 16px',
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  statValue: { fontSize: '20px', fontWeight: '700', color: C.text },
  statLabel: { fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' },

  tabs: { display: 'flex', gap: '0', borderBottom: `1px solid ${C.border}`, marginBottom: '20px' },
  tab: {
    background: 'none', border: 'none', color: C.muted, fontSize: '13px',
    fontWeight: '500', padding: '8px 18px', cursor: 'pointer',
    borderBottom: '2px solid transparent', marginBottom: '-1px',
    fontFamily: 'system-ui, sans-serif',
  },
  tabActive: { color: C.text, borderBottom: `2px solid ${C.teal}` },

  cards: { display: 'flex', flexDirection: 'column', gap: '12px' },

  card: {
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '16px 20px',
  },
  cardTop: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '10px',
  },
  cardMeta:  { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  typeBadge: {
    fontSize: '11px', fontWeight: '600', padding: '3px 9px',
    borderRadius: '4px', letterSpacing: '0.02em',
  },
  confidence: { fontSize: '11px', color: C.mutedDark },
  snoozeBadge: { fontSize: '11px', color: '#f59e0b', background: '#78350f', padding: '2px 8px', borderRadius: '4px' },

  savings:      { display: 'flex', alignItems: 'baseline', gap: '2px', flexShrink: 0 },
  savingsValue: { fontSize: '22px', fontWeight: '700', color: C.teal },
  savingsLabel: { fontSize: '12px', color: C.muted },

  cardTitle: {
    margin: '0 0 8px', fontSize: '14px', fontWeight: '600',
    color: C.text, lineHeight: '1.4',
  },
  cardBody: {
    margin: '0 0 14px', fontSize: '13px', color: '#9ca3af',
    lineHeight: '1.6',
  },
  cardActions: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },

  ghostBtn: {
    background: 'none', border: `1px solid ${C.border}`, color: C.text,
    borderRadius: '6px', padding: '5px 12px', fontSize: '12px',
    cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
    display: 'flex', alignItems: 'center',
  },

  snoozeMenu: {
    position: 'absolute', top: '100%', right: 0, marginTop: '4px',
    background: '#1e2329', border: `1px solid ${C.border}`,
    borderRadius: '6px', zIndex: 50, minWidth: '120px', overflow: 'hidden',
  },
  snoozeItem: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '8px 14px', background: 'none', border: 'none',
    color: C.text, fontSize: '13px', cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif',
  },
};
